/**
 * API Client service for Agile Tool Chest frontend
 * Provides typed methods for all backend API endpoints
 */

import type {
  ApiResponse,
  Room,
  Voter,
  RoomWithVoters,
  CreateRoomRequest,
  JoinRoomRequest,
  UpdateRoomRequest,
  SubmitVoteRequest,
  UpdateVoterRequest,
  VotingHistory
} from '../types/api';
import { ApiClientError } from '../types/api';
import type { Router } from 'vue-router';

// Error codes for join page query parameters
export enum JoinPageErrorCode {
  INVALID_SESSION = 'invalid_session'
}

/**
 * Handles 403 errors by clearing local state and redirecting to join flow.
 * This is called when the backend detects a missing/invalid user's session cookie 
 * since they joined the room.
 * 
 * IMPORTANT: The user's name is preserved so they don't have to re-enter it when rejoining.
 * 
 * @param roomKey - The room key to redirect to for rejoining
 * @param router - Vue Router instance for navigation
 */
export function handleSessionMismatch(roomKey: string, router: Router): void {
  // Preserve the user's name before clearing the voter session (Requirement 17.2)
  const storedVoter = localStorage.getItem('agile-tool-chest-current-voter');
  let preservedName: string | null = null;
  if (storedVoter) {
    try {
      const voter = JSON.parse(storedVoter);
      preservedName = voter.voterName;
    } catch {
      // Ignore parse errors
    }
  }
  
  // Clear the invalid voter session (Requirement 17.1)
  localStorage.removeItem('agile-tool-chest-current-voter');
  
  // Store the preserved name for the join form to use (Requirement 17.2, 17.5)
  if (preservedName) {
    localStorage.setItem('agile-tool-chest-preserved-voter-name', preservedName);
  }
  
  // Stop polling when invalid session is detected (Requirement 17.6)
  // We'll handle this asynchronously to avoid circular dependencies
  setTimeout(async () => {
    try {
      const { useRoomStore } = await import('../stores/room');
      const roomStore = useRoomStore();
      roomStore.stopPolling();
    } catch (error) {
      console.error('Failed to stop polling during invalid session:', error);
    }
  }, 0);
  
  // Redirect to join page with error message code (Requirements 17.3, 17.4)
  router.push({
    path: `/join/${roomKey}`,
    query: { error: JoinPageErrorCode.INVALID_SESSION }
  });
}

/**
 * Extracts the room key from an API endpoint path.
 * Returns null if the endpoint doesn't contain a room key.
 * 
 * @param endpoint - The API endpoint path (e.g., '/api/rooms/ABC123/voters/xyz')
 * @returns The room key or null if not found
 */
export function extractRoomKeyFromEndpoint(endpoint: string): string | null {
  const match = endpoint.match(/\/api\/rooms\/([^/]+)/);
  return match ? match[1] : null;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly timeout: number;
  private router: Router | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    this.timeout = 10000; // 10 second timeout
  }

  /**
   * Set the router instance for handling redirects
   * @param router - Vue Router instance
   */
  setRouter(router: Router): void {
    this.router = router;
  }

  /**
   * Base fetch wrapper with error handling
   */
  private async fetchWithTimeout<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await this.makeRequest(endpoint, options, controller.signal);
      clearTimeout(timeoutId);
      
      await this.handleResponseErrors(response, endpoint);
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.processError(error);
    }
  }

  /**
   * Makes the HTTP request with proper configuration
   */
  private async makeRequest(endpoint: string, options: RequestInit, signal: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };

    // Only set Content-Type when there's a request body.
    // GET/DELETE without a body don't need it, and omitting it
    // avoids unnecessary CORS preflight OPTIONS requests.
    if (options.body) {
      headers['Content-Type'] = 'application/json';
    }

    return fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      credentials: 'include', // Required for cross-origin cookie handling
      headers,
      signal,
    });
  }

  /**
   * Handles HTTP response errors and session mismatches
   */
  private async handleResponseErrors(response: Response, endpoint: string): Promise<void> {
    if (!response.ok) {
      const errorInfo = await this.parseErrorResponse(response);
      
      if (response.status === 403) {
        this.handleSessionMismatchError(endpoint);
      }
      
      throw new ApiClientError(errorInfo.message, response.status, errorInfo.code, errorInfo.details);
    }
  }

  /**
   * Parses error response JSON and extracts error information
   */
  private async parseErrorResponse(response: Response) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let code: string | undefined;
    let details: any = null;

    try {
      const errorData = await response.json();
      if (errorData.error?.message) {
        message = errorData.error.message;
      }
      code = errorData.error?.code;
      details = errorData.error?.details;
    } catch {
      // Use default message if JSON parsing fails
    }

    return { message, code, details };
  }

  /**
   * Handles 403 session mismatch errors by triggering redirect flow
   */
  private handleSessionMismatchError(endpoint: string): void {
    const roomKey = extractRoomKeyFromEndpoint(endpoint);
    if (roomKey && this.router) {
      handleSessionMismatch(roomKey, this.router);
    }
  }

  /**
   * Processes and converts various error types to ApiClientError
   */
  private processError(error: unknown): ApiClientError {
    if (error instanceof ApiClientError) {
      return error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      return new ApiClientError('Request timeout', 0);
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new ApiClientError('Network error - please check your connection', 0);
    }

    return new ApiClientError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      0
    );
  }

  // Room endpoints

  /**
   * Get room name (public endpoint, no authentication required)
   */
  async getRoomName(roomKey: string): Promise<string> {
    const response = await this.fetchWithTimeout<ApiResponse<{ roomName: string }>>(
      `/api/rooms/${roomKey}/name`
    );
    return response.data.roomName;
  }

  /**
   * Create a new room
   */
  async createRoom(request: CreateRoomRequest): Promise<Room> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      '/api/rooms',
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    return this.mapRoomDtoToRoom(response.data);
  }

  /**
   * Get room with voters
   */
  async getRoom(roomKey: string): Promise<RoomWithVoters> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      `/api/rooms/${roomKey}`
    );
    return this.mapRoomDtoToRoomWithVoters(response.data);
  }

  /**
   * Update room (subject)
   */
  async updateRoom(roomKey: string, request: UpdateRoomRequest): Promise<Room> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      `/api/rooms/${roomKey}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );
    return this.mapRoomDtoToRoom(response.data);
  }

  /**
   * Delete room
   */
  async deleteRoom(roomKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Voter endpoints

  /**
   * Join room (create voter)
   */
  async joinRoom(roomKey: string, request: JoinRoomRequest): Promise<Voter> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      `/api/rooms/${roomKey}/voters`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    const voter = this.mapVoterDtoToVoter(response.data);
    voter.roomKey = roomKey; // Set the roomKey since backend doesn't include it
    return voter;
  }

  /**
   * Remove voter
   */
  async removeVoter(roomKey: string, voterKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/voters/${voterKey}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Update voter (e.g., activeTab for crowd indicator)
   */
  async updateVoter(
    roomKey: string,
    voterKey: string,
    request: UpdateVoterRequest
  ): Promise<Voter> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      `/api/rooms/${roomKey}/voters/${voterKey}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );
    const voter = this.mapVoterDtoToVoter(response.data);
    voter.roomKey = roomKey;
    return voter;
  }

  // Vote endpoints

  /**
   * Submit estimation poker vote
   */
  async submitEstimationVote(
    roomKey: string,
    voterKey: string,
    request: SubmitVoteRequest
  ): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/voters/${voterKey}/estimation-poker/vote`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Submit confidence vote
   */
  async submitConfidenceVote(
    roomKey: string,
    voterKey: string,
    request: SubmitVoteRequest
  ): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/voters/${voterKey}/confidence-vote`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  // Vote control endpoints

  /**
   * Reveal estimation poker votes
   */
  async revealEstimationVotes(roomKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/estimation-poker/reveal`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Reveal confidence votes
   */
  async revealConfidenceVotes(roomKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/confidence-vote/reveal`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Clear estimation poker votes
   */
  async clearEstimationVotes(roomKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/estimation-poker`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Clear confidence votes
   */
  async clearConfidenceVotes(roomKey: string): Promise<void> {
    await this.fetchWithTimeout<ApiResponse<void>>(
      `/api/rooms/${roomKey}/confidence-vote`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Start estimation poker timer
   * Sets the timer start timestamp if not already set (idempotent)
   */
  async startEstimationPokerTimer(roomKey: string): Promise<Room> {
    const response = await this.fetchWithTimeout<ApiResponse<any>>(
      `/api/rooms/${roomKey}/estimation-poker/timer`,
      {
        method: 'POST',
      }
    );
    return this.mapRoomDtoToRoom(response.data);
  }

  /**
   * Get voting history for a room
   * Returns historical voting round records ordered from most recent to oldest
   */
  async getVotingHistory(roomKey: string): Promise<VotingHistory> {
    const response = await this.fetchWithTimeout<ApiResponse<VotingHistory>>(
      `/api/rooms/${roomKey}/voting-history`
    );
    return response.data;
  }

  /**
   * Map backend RoomDto to frontend Room interface
   */
  private mapRoomDtoToRoom(roomDto: any): Room {
    return {
      roomKey: roomDto.roomKey,
      roomName: roomDto.roomName,
      subject: roomDto.subject,
      estimationMode: roomDto.estimationMode ?? 'single',
      dualEstimateLabels: roomDto.dualEstimateLabels,
      estimationPokerState: {
        votingRound: roomDto.estimationPokerVotingRound,
        votesRevealed: roomDto.estimationPokerRevealVotes,
      },
      confidenceVoteState: {
        votingRound: roomDto.confidenceVoteVotingRound,
        votesRevealed: roomDto.confidenceVoteRevealVotes,
      },
      estimationPokerTimerStartedAt: roomDto.estimationPokerTimerStartedAt ?? null,
      currentServerTime: roomDto.currentServerTime ?? new Date().toISOString(),
      createdAt: roomDto.createdAt,
      updatedAt: roomDto.updatedAt,
    };
  }

  /**
   * Map backend VoterDto to frontend Voter interface
   */
  private mapVoterDtoToVoter(voterDto: any): Voter {
    const voter: Voter = {
      voterKey: voterDto.voterKey,
      roomKey: voterDto.roomKey || '', // Backend doesn't include roomKey in VoterDto
      voterName: voterDto.userName,
      activeTab: voterDto.activeTab ?? null,
      createdAt: voterDto.createdAt,
      updatedAt: voterDto.updatedAt,
    };

    // Add estimation poker vote if present (both vote value and voting round must exist)
    if (voterDto.estimationPokerVote !== undefined && voterDto.estimationPokerVotingRound !== undefined) {
      voter.estimationPokerVote = {
        value: voterDto.estimationPokerVote,
        votingRound: voterDto.estimationPokerVotingRound,
      };
    }

    // Add dual estimate votes if present
    if (voterDto.estimationPokerDualVotes) {
      voter.estimationPokerDualVotes = voterDto.estimationPokerDualVotes
    }

    // Add confidence vote if present (both vote value and voting round must exist)
    if (voterDto.confidenceVote !== undefined && voterDto.confidenceVoteVotingRound !== undefined) {
      voter.confidenceVote = {
        value: voterDto.confidenceVote,
        votingRound: voterDto.confidenceVoteVotingRound,
      };
    }

    return voter;
  }

  /**
   * Map backend RoomDto (with voters) to frontend RoomWithVoters interface
   */
  private mapRoomDtoToRoomWithVoters(roomDto: any): RoomWithVoters {
    const room = this.mapRoomDtoToRoom(roomDto);
    const voters = (roomDto.voters || []).map((voterDto: any) => {
      // Set roomKey for each voter since backend doesn't include it
      const voter = this.mapVoterDtoToVoter(voterDto);
      voter.roomKey = room.roomKey;
      return voter;
    });

    return { room, voters };
  }
}

// Export singleton instance
export const apiClient = new ApiClient();